import COS from 'cos-js-sdk-v5';
import { http } from '@/utils';

const Api = {
  GetTCosToken: '/GetTCosToken',
};


/**
 * STS temporary credentials interface
 */
export interface STSCredentials {
  TmpSecretId: string;
  TmpSecretKey: string;
  SecurityToken: string;
  ExpiredTime: number;
  StartTime: number;
  CosKey: string;
  Bucket: string;
  Region: string;
}


/**
 * Upload options interface
 */
export interface UploadOptions {
  file: File;
  /** Custom file path prefix, e.g., 'images/', 'documents/' */
  prefix?: string;
  /** Custom file name, if not provided, a unique name will be generated */
  fileName?: string;
  /** Upload progress callback */
  onProgress?: (percent: number) => void;
}

/**
 * Upload result interface
 */
export interface UploadResult {
  /** File URL */
  url: string;
  /** File key in COS */
  key: string;
  /** Bucket name */
  bucket: string;
  /** Region */
  region: string;
}

const PDF_CONTENT_TYPE = 'application/pdf';

function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex >= 0 ? fileName.substring(lastDotIndex).toLowerCase() : '';
}

function getUploadHeaders(file: File, finalFileName: string): Record<string, string> | undefined {
  const extension = getFileExtension(finalFileName || file.name);
  const contentType = file.type || (extension === '.pdf' ? PDF_CONTENT_TYPE : '');

  if (extension === '.pdf' || contentType === PDF_CONTENT_TYPE) {
    const encodedFileName = encodeURIComponent(finalFileName || file.name);
    return {
      'Content-Type': PDF_CONTENT_TYPE,
      'Content-Disposition': `inline; filename="${encodedFileName}"; filename*=UTF-8''${encodedFileName}`,
    };
  }

  if (contentType) {
    return {
      'Content-Type': contentType,
    };
  }

  return undefined;
}

/**
 * Get STS temporary credentials from backend
 */
export async function getSTSCredentials(fileName: string): Promise<STSCredentials> {
  const response = await http.post<STSCredentials>(Api.GetTCosToken, { FileName: fileName });
  return response.Data;
}

/**
 * Initialize COS instance with pre-fetched STS credentials
 * @param stsData - Pre-fetched STS credentials
 */
export function initCOS(stsData: STSCredentials): COS {
  // Create a new COS instance for each upload to avoid credential mismatch
  const cos = new COS({
    // Use getAuthorization callback
    getAuthorization: async (_options, callback) => {
      try {
        if (stsData) {
          callback({
            TmpSecretId: stsData.TmpSecretId,
            TmpSecretKey: stsData.TmpSecretKey,
            SecurityToken: stsData.SecurityToken,
            StartTime: stsData.StartTime,
            ExpiredTime: stsData.ExpiredTime,
          });
        } else {
          callback(new Error('No STS credentials available'));
        }
      } catch (error) {
        console.error('Failed to get STS credentials:', error);
        callback(new Error('Failed to get STS credentials'));
      }
    },
  });

  return cos;
}

/**
 * Generate unique file name
 */
export function generateUniqueFileName(originalName: string): string {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 10);
  const ext = originalName.substring(originalName.lastIndexOf('.'));
  return `${timestamp}_${randomStr}${ext}`;
}

/**
 * Upload file to Tencent Cloud COS
 */
export async function uploadToCOS(options: UploadOptions): Promise<UploadResult> {
  const { file, prefix = '', fileName, onProgress } = options;

  // Use provided fileName or generate a unique one
  const finalFileName = fileName || generateUniqueFileName(file.name);
  const fullPath = `${prefix}${finalFileName}`.replace(/^\/+/, '');
  const headers = getUploadHeaders(file, finalFileName);

  // Get STS credentials to retrieve bucket and region info
  const stsData = await getSTSCredentials(fullPath);
  const { Bucket, Region, CosKey } = stsData;

  // Initialize COS instance with pre-fetched STS data
  const cos = initCOS(stsData);

  return new Promise((resolve, reject) => {
    cos.uploadFile(
      {
        Bucket: Bucket,
        Region: Region,
        Key: CosKey,
        Body: file,
        ...({ Headers: headers } as any),
        SliceSize: 1024 * 1024 * 5, // Use multipart upload for files > 5MB
        onProgress: (progressData) => {
          const percent = Math.round(progressData.percent * 100);
          onProgress?.(percent);
        },
      },
      (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve({
            url: `https://${data.Location}`,
            key: CosKey,
            bucket: Bucket,
            region: Region,
          });
        }
      },
    );
  });
}

/**
 * Upload multiple files to COS
 */
export async function uploadMultipleToCOS(
  files: File[],
  options?: Omit<UploadOptions, 'file'> & {
    onTotalProgress?: (percent: number, current: number, total: number) => void;
  },
): Promise<UploadResult[]> {
  const results: UploadResult[] = [];
  const total = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const result = await uploadToCOS({
      file,
      prefix: options?.prefix,
      onProgress: (percent) => {
        options?.onTotalProgress?.(percent, i + 1, total);
      },
    });
    results.push(result);
  }

  return results;
}

/**
 * Delete file from COS
 */
export async function deleteFromCOS(key: string): Promise<void> {
  const fullPath = key.replace(/^\/+/, '');
  const stsData = await getSTSCredentials(fullPath);
  const { Bucket, Region, CosKey } = stsData;
  const cos = initCOS(stsData);

  return new Promise((resolve, reject) => {
    cos.deleteObject(
      {
        Bucket: Bucket,
        Region: Region,
        Key: CosKey,
      },
      (err) => {
        if (err) {
          console.error('COS delete error:', err);
          reject(err);
        } else {
          resolve();
        }
      },
    );
  });
}

/**
 * Check if file exists in COS
 */
export async function checkFileExists(key: string): Promise<boolean> {
  const fullPath = key.replace(/^\/+/, '');
  const stsData = await getSTSCredentials(fullPath);
  const { Bucket, Region, CosKey } = stsData;
  const cos = initCOS(stsData);

  return new Promise((resolve) => {
    cos.headObject(
      {
        Bucket: Bucket,
        Region: Region,
        Key: CosKey,
      },
      (err) => {
        resolve(!err);
      },
    );
  });
}

export default {
  initCOS,
  getSTSCredentials,
  uploadToCOS,
  uploadMultipleToCOS,
  deleteFromCOS,
  checkFileExists,
  generateUniqueFileName,
};
